import { toApiError } from '../errorHandling.js';
import { API_BASE_URL, buildApiUrl } from '../../config/api.js';

export interface ArticleSection {
  index: number;
  title: string;
  content: string;
  editedContent?: string;
  wordCount: number;
  metadata?: {
    sectionDescription?: string;
    targetWordCount?: number;
  };
}

export interface Article {
  id: string;
  title: string;
  fullContent: string;
  sections: ArticleSection[];
  references: Array<{ url: string }>;
  outline: any;
  lastGeneratedIndex: number;
  articleBriefId?: string;
  metadata?: {
    styleGuide?: string;
  };
}

export interface ArticleListResponse {
  count: number;
  data: Article[];
}

export interface ArticleSavePayload {
  id?: string;
  title: string;
  fullContent: string;
  sections: ArticleSection[];
  references: Array<{ url: string }>;
  outline: any;
  lastGeneratedIndex: number;
  articleBriefId?: string;
  metadata?: {
    styleGuide?: string;
  };
}

export interface ArticleListFilters {
  briefId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

/**
 * Service for managing articles
 */
class ArticleService {
  /**
   * Get an article by ID
   */
  async getById(
    id: string,
    getToken?: () => Promise<string | null>
  ): Promise<Article> {
    try {
      const headers: HeadersInit = {};

      // Add auth token if available
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/articles/${id}`, { headers });

      if (!response.ok) {
        throw new Error(`Failed to load article: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error loading article:', error);
      throw toApiError(error);
    }
  }

  /**
   * Save an article (create or update)
   */
  async save(
    data: ArticleSavePayload,
    getToken?: () => Promise<string | null>
  ): Promise<Article> {
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };

      // Add auth token if available
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/articles/save`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`Failed to save article: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error saving article:', error);
      throw toApiError(error);
    }
  }

  /**
   * Create a new article (uses save endpoint)
   */
  async create(
    data: Omit<ArticleSavePayload, 'id'>,
    getToken?: () => Promise<string | null>
  ): Promise<Article> {
    try {
      console.log('📤 Sending article creation request:', {
        title: data.title,
        articleBriefId: data.articleBriefId,
        sectionsCount: data.sections?.length || 0,
        firstSection: data.sections?.[0] ? {
          index: data.sections[0].index,
          title: data.sections[0].title,
          content: data.sections[0].content?.substring(0, 50)
        } : 'No sections'
      });

      const headers: HeadersInit = { 'Content-Type': 'application/json' };

      // Add auth token if available
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/articles/save`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Article creation failed:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`Failed to create article: ${response.statusText}`);
      }

      const result = await response.json();
      const article = result.data || result;

      console.log('📥 Article creation response:', {
        id: article.id,
        title: article.title,
        sectionsCount: article.sections?.length || 0,
        firstSection: article.sections?.[0] ? {
          index: article.sections[0].index,
          title: article.sections[0].title,
          content: article.sections[0].content?.substring(0, 50)
        } : 'No sections'
      });

      return article;
    } catch (error) {
      console.error('❌ Error creating article:', error);
      throw toApiError(error);
    }
  }

  /**
   * Delete an article by ID
   */
  async delete(
    id: string,
    getToken?: () => Promise<string | null>
  ): Promise<void> {
    try {
      const headers: HeadersInit = {};

      // Add auth token if available
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/articles/${id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to delete article: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      throw toApiError(error);
    }
  }

  /**
   * List articles with optional filters
   */
  async list(
    filters: ArticleListFilters = {},
    getToken?: () => Promise<string | null>
  ): Promise<ArticleListResponse> {
    try {
      console.log('🔍 [ARTICLE SERVICE] Fetching articles list with filters:', filters);

      const headers: HeadersInit = {};

      // Add auth token if available
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      // Build query string manually for relative URLs
      const params = new URLSearchParams();
      if (filters.briefId) {
        params.set('briefId', filters.briefId);
      }
      if (filters.status) {
        params.set('status', filters.status);
      }
      if (filters.limit) {
        params.set('limit', filters.limit.toString());
      }
      if (filters.offset) {
        params.set('offset', filters.offset.toString());
      }

      const queryString = params.toString();
      const url = buildApiUrl(`/api/articles/list${queryString ? `?${queryString}` : ''}`);

      console.log('📡 [ARTICLE SERVICE] Calling API:', url);
      const response = await fetch(url, { headers });

      if (!response.ok) {
        console.error('❌ [ARTICLE SERVICE] API call failed:', response.status, response.statusText);
        throw new Error(`Failed to list articles: ${response.statusText}`);
      }

      const result = await response.json();
      
      console.log('📥 [ARTICLE SERVICE] API Response:', {
        count: result.count,
        articlesReturned: result.data?.length || 0
      });
      
      // Log detailed info about first article
      if (result.data && result.data.length > 0) {
        const firstArticle = result.data[0];
        console.log('📄 [ARTICLE SERVICE] First article data:', {
          id: firstArticle.id,
          title: firstArticle.title,
          hasSections: Boolean(firstArticle.sections),
          sectionsIsArray: Array.isArray(firstArticle.sections),
          sectionsLength: firstArticle.sections?.length || 0,
          sectionsData: firstArticle.sections ? firstArticle.sections.map((s: any) => ({
            sectionNumber: s.sectionNumber,
            title: s.title,
            hasContent: Boolean(s.content),
            contentLength: s.content?.length || 0
          })) : 'NO SECTIONS IN RESPONSE'
        });
      } else {
        console.log('⚠️ [ARTICLE SERVICE] No articles in response');
      }
      
      return result;
    } catch (error) {
      console.error('❌ [ARTICLE SERVICE] Error listing articles:', error);
      throw toApiError(error);
    }
  }

  /**
   * Generate a section for an article
   * Returns an EventSource for streaming the generation process
   */
  generateSectionStream(_briefId: string, sectionIndex: number, styleGuide: string, articleId?: string): EventSource {
    const url = buildApiUrl(`/api/articles/${articleId}/sections/${sectionIndex}/regenerate`);
    
    // For POST requests with body, we can't use EventSource directly
    // This is a limitation - we need to use fetch with ReadableStream instead
    // For now, let's create a simple alternative that works with the existing backend
    
    const requestBody = {
      styleGuide: styleGuide,
      prompt: undefined
    };

    // Create a custom implementation that mimics EventSource but uses fetch
    const controller = new AbortController();
    const eventSource: any = {
      close: () => controller.abort(),
      onopen: null,
      onmessage: null,
      onerror: null
    };

    // Make the actual API call
    fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal
    }).then(async (response) => {
      if (!response.ok) {
        eventSource.onerror?.(new Error(`HTTP error! status: ${response.status}`));
        return;
      }

      eventSource.onopen?.();

      // Handle SSE response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      // Helper to process SSE line (extracted to reduce nesting)
      const processLine = (line: string) => {
        if (line.startsWith('data: ')) {
          const data = line.substring(6);
          eventSource.onmessage?.({ data });
        }
      };

      if (reader) {
        const pump = () => {
          reader.read().then(({ done, value }) => {
            if (done || controller.signal.aborted) {
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              processLine(line);
            }

            pump();
          }).catch(error => {
            if (!controller.signal.aborted) {
              eventSource.onerror?.(error);
            }
          });
        };
        pump();
      }
    }).catch(error => {
      if (!controller.signal.aborted) {
        eventSource.onerror?.(error);
      }
    });

    return eventSource;
  }
}

// Export singleton instance
export const articleService = new ArticleService();